import { ChallengeTable } from "@components/challengeTable/challengeTable";
import { DynamicFilterBar } from "@components/filter/dynamicFilterBar";
import { Flex } from "@components/flex/flex";
import { HeaderContent } from "@components/headerContent/headerContent";
import { TableContainer } from "@components/table/tableContainer";
import { ChallengeFragment } from "@graphql2/types";
import { ChallengePageProps } from "@pages/challengesPage/challengesPageContainer";
import { getFrontendApplication } from "@utils/applicationUtils";
import { translateChallenges } from "@utils/mappers/challengeMapper";
import { modifyConditionsIds } from "@utils/modifyConditionsIds";
import { readFile } from "@utils/readFile";
import { useUpdateQuery } from "@utils/useUrlQuery";
import { getCurrentTablePage, getPageSize, getUpdateUrlQueryParams, onTableChange } from "@utils/withPagingAndSort";
import { Alert, Button, Card, Upload } from "antd";
import { TablePaginationConfig as PaginationConfig } from "antd/lib/table";
import { SorterResult } from "antd/lib/table/interface";
import { languages } from "environment";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { ACTIVITY_TYPES_CHALLNGE } from "../../constants";

export const ChallengePage: React.FC<ChallengePageProps> = (props: ChallengePageProps) => {
    const {
        history,
        removeChallenge,
        count,
        data,
        applications,
        paging
    } = props;

    const intl = useIntl();

    const [uploadError, setUploadError] = useState<string>();
    const updateUrlQuery = useUpdateQuery({ replace: true });

    const translatedChallenges = React.useMemo(
        () => translateChallenges(data?.challenges || []),
        [data]
    );

    const sortedApplications = React.useMemo(
        () => {
            if (!applications || !applications.applications) {
                return;
            }

            const frontendApplications = getFrontendApplication(applications.applications);

            if (!frontendApplications) {
                return;
            }

            return [{
                ...frontendApplications,
                roles: [...frontendApplications.roles].sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
            }];
        },
        [applications && applications.applications]
    );

    const handleUpload = async (info: { file: File; }) => {
        try {
            const importData = await readFile<ChallengeFragment>(info.file);
            history.push("/challenges/add/general", { import: modifyConditionsIds(importData) });
        } catch (error) {
            setUploadError("upload.tryAgain");
        }
    };

    const updateTableSettings = (pagination: PaginationConfig, filters: Record<any, string[]>, sorter: SorterResult<any>) => {
        updateUrlQuery(getUpdateUrlQueryParams(onTableChange(pagination, filters, sorter)));
    };

    const removeChallengeFn = (id: string) => {
        return removeChallenge({ variables: { id } });
    };

    return (
        <div>
            <HeaderContent>
                <Flex justifyContent="space-between">
                    <FormattedMessage
                        id="challenges"
                        tagName="h1"
                    />
                    <div>
                        <Upload
                            accept="application/json"
                            customRequest={handleUpload}
                        >
                            <Button
                                className="headerButton"
                                type="primary"
                            >
                                <FormattedMessage id="challenge.import" />
                            </Button>
                        </Upload>
                        <Link to="/challenges/add/general">
                            <Button
                                className="headerButton"
                                type="primary"
                            >
                                <FormattedMessage id="challenge.add" />
                            </Button>
                        </Link>
                    </div>
                </Flex>
            </HeaderContent>

            <Card>
                {uploadError && (
                    <Alert
                        message={<FormattedMessage id={uploadError} />}
                        type="error"
                    />
                )}
                <DynamicFilterBar fields={[
                    {
                        name: "textSearch",
                        type: "input",
                        search: true,
                        placeholder: intl.formatMessage({ id: "filter.textSearch" })
                    },
                    {
                        name: "activity",
                        type: "select",
                        options: ACTIVITY_TYPES_CHALLNGE.map(at => ({
                            value: at.key,
                            name: at.value
                        })),
                        placeholder: intl.formatMessage({ id: "challenges.filter.activityType" }),
                        span: 5
                    },
                    {
                        name: "enabledLocales",
                        type: "select",
                        multiple: true,
                        placeholder: intl.formatMessage({ id: "filter.enabledLocales" }),
                        options: languages.map(lang => ({
                            value: lang,
                            name: lang
                        })),
                        span: 5
                    },
                    {
                        name: "userRoles",
                        type: "select",
                        placeholder: intl.formatMessage({ id: "contents.filter.userRoles" }),
                        options: (sortedApplications || []).map(application => ({
                            name: application.name,
                            options: application.roles.map(role => ({
                                name: role.name,
                                value: `${role.name}_${application.id}_${role.id}`
                            }))
                        })),
                        multiple: true
                    }
                ]}
                />
                <TableContainer
                    applications={applications?.applications || []}
                    dataSource={translatedChallenges}
                    handlers={{
                        removeChallenge: removeChallengeFn
                    }}
                    loading={data?.loading}
                    pagination={{
                        current: getCurrentTablePage(paging),
                        pageSize: getPageSize(paging),
                        total: count?.challengeCount
                    }}
                    TableComponent={ChallengeTable}
                    onChange={updateTableSettings}
                />
            </Card>
        </div>
    );
};
